/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable import/no-named-as-default */
'use client'

import { Copy, CopyCheck, Share } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@components/ui/drawer'
import { useEffect, useState } from 'react'

import Image from 'next/image'
import { StandardEvent } from '@lib/analytics'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@lib/utils'
import { usePostHog } from 'posthog-js/react'
import { useUtmInitialValues } from '@components/form/utm-fields'

const ShareInsight = ({
  title,
  slug,
  author,
}: {
  title: string
  slug: string
  author: string
}) => {
  const utmInitialValues = useUtmInitialValues()
  const posthog = usePostHog()
  const [navigatorCanShare, setNavigatorCanShare] = useState(true)
  const [flag, setFlag] = useState('')

  useEffect(() => {
    if (!navigator.canShare) {
      setNavigatorCanShare(false)
    }
  }, [])

  useEffect(() => {
    if (posthog.__loaded) {
      setFlag(posthog.getFeatureFlag('share-insight-cta') as string)
    }
  }, [posthog.__loaded])

  const handleOnShare = (medium: string) => {
    posthog.capture(StandardEvent.Share, {
      post: title,
      author,
      medium,
      ...utmInitialValues,
    })
  }

  const navigatorShare = () => {
    handleOnShare('other')
    navigator
      .share({
        title,
        text: 'Just read this latest article published by V2 AI and thought you might be interested, have a read',
        url: `${shareUrl}&utm_medium=other`,
      })
      .then(() => {
        handleOnShare('other')
        return true
      })
      .catch((_) => {
        return false
      })
  }

  const [copyIcon, setCopyIcon] = useState(<></>)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      setCopyIcon(<CopyCheck className='mx-auto' strokeWidth={1} size={50} />)
    } else {
      setCopyIcon(<Copy className='mx-auto' strokeWidth={1} size={50} />)
    }
  }, [copied])

  const copyToClipboard = () => {
    handleOnShare('clipboard')
    navigator.clipboard.writeText(`${shareUrl}&utm_medium=clipboard`)
    setCopied(true)

    setTimeout(() => {
      setCopied(false)
    }, 3000)
  }

  const shareUrl = `https://www.v2.ai/insights/${slug}?utm_source=share`

  return flag === 'navigator' ? (
    <Drawer>
      <DrawerTrigger
        className={cn([buttonVariants(), 'mx-auto text-xl font-semibold'])}
      >
        <Share className='mr-2' />
        Share
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className='mx-auto my-4'>
            How would you like to share?
          </DrawerTitle>
        </DrawerHeader>
        <div className='mx-auto mb-20 flex-col items-center'>
          <div className='mx-auto mb-2 flex items-center'>
            <a
              target='about:blank'
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(
                `${shareUrl}&utm_medium=linkedin`,
              )}`}
              rel='noopener noreferrer'
              onMouseDown={() => {
                handleOnShare('linkedin')
              }}
            >
              <Image
                src='/assets/svg/linked-in.svg'
                className='mx-5'
                height={76}
                width={76}
                alt='linkedin'
                unoptimized
              />
            </a>
            <a
              href={`mailto:?subject=Check out this article:${title}&body=Just read this latest article published by V2 AI and thought you might be interested, have a read ${shareUrl}&utm_medium=email`}
              onMouseDown={() => {
                handleOnShare('email')
              }}
            >
              <Image
                src='/assets/svg/mail.svg'
                className='mx-5'
                height={76}
                width={76}
                alt='mail'
                unoptimized
              />
            </a>
            {navigatorCanShare ? (
              <button
                type='button'
                className='mx-5 h-[76px] w-[76px] rounded-lg hover:bg-gray-100'
                onMouseDown={() => {
                  navigatorShare()
                }}
              >
                <Share className='mx-auto' />
              </button>
            ) : (
              <button
                type='button'
                className='mx-5 h-[76px] w-[76px] rounded-lg hover:bg-gray-100'
                onMouseDown={() => {
                  copyToClipboard()
                }}
              >
                {copyIcon}
              </button>
            )}
          </div>
          <div className='mx-auto flex items-center'>
            <div className='mx-5 w-full text-center'>Linkedin</div>
            <div className='mx-5 w-full text-center'>Email</div>
            {navigatorCanShare ? (
              <div className='mx-5 w-full text-center'>More</div>
            ) : (
              <div className='mx-5 w-full text-center'>Copy Link</div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <div className='mx-auto flex items-center'>
      <a
        target='about:blank'
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(
          `${shareUrl}&utm_medium=linkedin`,
        )}`}
        rel='noopener noreferrer'
        onMouseDown={() => {
          handleOnShare('linkedin')
        }}
      >
        <Image
          src='/assets/svg/linked-in.svg'
          height={76}
          width={76}
          alt='linkedin'
          unoptimized
        />
      </a>
      <a
        href={`mailto:?subject=Check out this article:${title}&body=Just read this latest article published by V2 AI and thought you might be interested, have a read ${shareUrl}&utm_medium=email`}
        onMouseDown={() => {
          handleOnShare('email')
        }}
      >
        <Image
          src='/assets/svg/mail.svg'
          height={76}
          width={76}
          alt='mail'
          className='ml-5'
          unoptimized
        />
      </a>
    </div>
  )
}

export default ShareInsight
